import { PlPopover } from "./pl--popover.js"

/**
 * PlDropdown - Custom element for dropdown menus
 * Extends PlPopover with menu-specific keyboard navigation
 */
class PlDropdown extends PlPopover {
  _menu = null
  _menuItems = []
  _activeIndex = -1

  connectedCallback() {
    super.connectedCallback()
    // Wait for parent init, then init menu
    this.addEventListener(
      "pl-popover:initialized",
      () => {
        this._initMenu()
      },
      { once: true }
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  _initMenu() {
    this._menu = this._content?.querySelector('[role="menu"]')
    if (!this._menu) return

    this._attachMenuListeners()
  }

  _attachMenuListeners() {
    // Mouse hover
    this._menu.addEventListener("mousemove", (e) => {
      const item = e.target.closest('[role^="menuitem"]')
      if (item) {
        const index = this._getMenuItems().indexOf(item)
        if (index > -1) this._setActiveItem(index)
      }
    })

    // Click to close
    this._menu.addEventListener("click", (e) => {
      if (e.target.closest('[role^="menuitem"]')) {
        this.close()
      }
    })
  }

  _getMenuItems() {
    return Array.from(this._menu.querySelectorAll('[role^="menuitem"]')).filter(
      (item) => !item.disabled && item.getAttribute("aria-disabled") !== "true"
    )
  }

  _setActiveItem(index) {
    // Remove previous
    if (this._activeIndex > -1) {
      this._menuItems[this._activeIndex]?.classList.remove("active")
    }

    // Set new
    this._activeIndex = index
    if (index > -1 && this._menuItems[index]) {
      this._menuItems[index].classList.add("active")
      this._trigger.setAttribute("aria-activedescendant", this._menuItems[index].id || "")
    } else {
      this._trigger.removeAttribute("aria-activedescendant")
    }
  }

  handleKeydown(event) {
    // Escape handled by parent
    if (event.key === "Escape") {
      return super.handleKeydown(event)
    }

    const isOpen = this.isOpen
    this._menuItems = this._getMenuItems()

    // Closed: open with arrow keys
    if (!isOpen) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.open()
        setTimeout(() => this._setActiveItem(0), 0)
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        this.open()
        setTimeout(() => this._setActiveItem(this._menuItems.length - 1), 0)
      }
      return
    }

    // Open: navigate menu
    if (this._menuItems.length === 0) return

    let nextIndex = this._activeIndex

    switch (event.key) {
      case "ArrowDown":
        nextIndex =
          this._activeIndex === -1 ? 0 : Math.min(this._activeIndex + 1, this._menuItems.length - 1)
        break
      case "ArrowUp":
        nextIndex =
          this._activeIndex === -1 ? this._menuItems.length - 1 : Math.max(this._activeIndex - 1, 0)
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = this._menuItems.length - 1
        break
      case "Enter":
      case " ":
        if (this._activeIndex > -1) {
          this._menuItems[this._activeIndex]?.click()
          this.close()
        }
        return
      default:
        return // Let other keys bubble
    }

    if (nextIndex !== this._activeIndex) {
      event.preventDefault()
      this._setActiveItem(nextIndex)
    }
  }

  close(focusOnTrigger = true) {
    this._setActiveItem(-1)
    super.close(focusOnTrigger)
  }
}

if (!customElements.get("pl-dropdown")) {
  customElements.define("pl-dropdown", PlDropdown)
}

export { PlDropdown }
